import * as React from 'react';

import {
  enumCurrencyCode,
  enumPaymentTerm,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import {
  ContentSection,
  DatePicker,
  Grid,
  NumberField,
  TextArea,
  TextField,
  type DateValue,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $currentDraftInvoiceInfo,
  $effectiveInvoiceInfo,
  DraftInvoiceInfo,
} from '../../_state/invoiceDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FinanceDetailLayout } from '../_layouts/FinanceDetailLayout';
import { InvoiceStatusBadge } from '../_shared/InvoiceStatusBadge';

import { InvoiceDetailFooter } from './InvoiceDetailFooter';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { anyCalendarDateToCalendarDate } from '~/shared/utils/dateTime';
import { toMoney } from '~/shared/utils/money';

type Props = {
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ INVOICE TAB INFO ⚛️ ⚛️ ⚛️
//
export const InvoiceTabInfo: React.FC<Props> = ({ disabled = false }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const effectiveInvoice = useAtomValue($effectiveInvoiceInfo);
  const setDraftInvoiceInfo = useSetAtom($currentDraftInvoiceInfo);

  // Callbacks
  const handleFieldChange = React.useCallback(
    (updates: Partial<DraftInvoiceInfo>) => {
      if (!effectiveInvoice) return;

      setDraftInvoiceInfo({
        documentDate: effectiveInvoice.documentDate,
        documentNumber: effectiveInvoice.documentNumber,
        dueDate: effectiveInvoice.dueDate,
        paymentTerm: effectiveInvoice.paymentTerm,
        notes: effectiveInvoice.notes,
        amount: effectiveInvoice.amount,
        facilityId: effectiveInvoice.facilityId,
        ...updates,
      });
    },
    [effectiveInvoice, setDraftInvoiceInfo],
  );

  if (!effectiveInvoice) {
    return (
      <div className="p-4 text-neutral/60">
        {t.invoices.detail.empty.noData}
      </div>
    );
  }

  return (
    <FinanceDetailLayout
      className="@InvoiceTabInfo h-auto"
      footer={<InvoiceDetailFooter />}
    >
      <ContentSection
        title={t.invoices.detail.sections.basicInfo}
        titleClassName="text-lg font-semibold text-neutral/90"
      >
        <Grid gap4 cols4 className="items-start">
          <TextField
            label={t.invoices.detail.fields.type}
            value={
              t.enums.invoiceType[effectiveInvoice.type] ??
              effectiveInvoice.type
            }
            isDisabled
          />
          <TextField
            label={t.invoices.detail.fields.status}
            leftAdornment={
              <InvoiceStatusBadge status={effectiveInvoice.status} />
            }
            isDisabled
          />
          <SelectFacility
            label={t.invoices.detail.fields.facility}
            value={effectiveInvoice.facilityId ?? undefined}
            onChange={(value) =>
              handleFieldChange({ facilityId: value ?? null })
            }
            isDisabled={disabled}
          />
          <TextField
            label={t.invoices.detail.fields.documentNumber}
            value={effectiveInvoice.documentNumber ?? ''}
            onChange={(value) =>
              handleFieldChange({ documentNumber: value || null })
            }
            isDisabled={disabled}
          />
          <DatePicker
            label={t.invoices.detail.fields.documentDate}
            value={effectiveInvoice.documentDate as DateValue | undefined}
            onChange={(value) =>
              handleFieldChange({
                documentDate: anyCalendarDateToCalendarDate(value ?? undefined),
              })
            }
            isDisabled={disabled}
          />
          <DatePicker
            label={t.invoices.detail.fields.dueDate}
            value={effectiveInvoice.dueDate as DateValue | undefined}
            onChange={(value) =>
              handleFieldChange({
                dueDate: anyCalendarDateToCalendarDate(value ?? undefined),
              })
            }
            isDisabled={disabled}
          />
          <SelectEnum<PaymentTerm>
            label={t.invoices.detail.fields.paymentTerm}
            enumValue={enumPaymentTerm}
            value={effectiveInvoice.paymentTerm ?? undefined}
            onChange={(value) =>
              handleFieldChange({ paymentTerm: value ?? null })
            }
            isDisabled={disabled}
          />
          <NumberField
            label={t.invoices.detail.fields.amount}
            value={effectiveInvoice.amount?.amount ?? undefined}
            onChange={(value) =>
              handleFieldChange({
                amount:
                  value != null
                    ? toMoney({
                        amount: value,
                        currencyCode:
                          effectiveInvoice.amount?.currencyCode ??
                          enumCurrencyCode.USD,
                      })
                    : null,
              })
            }
            formatOptions={{
              style: 'currency',
              currency:
                effectiveInvoice.amount?.currencyCode ?? enumCurrencyCode.USD,
            }}
            isDisabled={disabled}
          />
          <TextField
            label={t.invoices.detail.fields.currency}
            value={
              effectiveInvoice.amount?.currencyCode ?? enumCurrencyCode.USD
            }
            isDisabled
          />

          <div className="col-span-4">
            <TextArea
              label={t.invoices.detail.fields.notes}
              value={effectiveInvoice.notes ?? ''}
              onChange={(value) => handleFieldChange({ notes: value || null })}
              rows={4}
              isDisabled={disabled}
            />
          </div>
        </Grid>
      </ContentSection>
    </FinanceDetailLayout>
  );
};
